/**
 * Sidebar — navigation, dynamically populated from enabled plugins.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { usePluginRegistry } from '@/infra/plugin-registry';
import { WebRoute } from '@/infra/types/domain';
import { RootState } from '@/infra/store';

interface SidebarLink {
  path: string;
  label: string;
  order: number;
}

// Core routes (always present)
const CORE_ROUTES: SidebarLink[] = [
  { path: '/', label: 'Todos', order: 0 },
  { path: '/calendar', label: 'Calendar', order: 1 },
];

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link';
}

export interface SidebarProps {
  open?: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ open = true, onNavigate }: SidebarProps = {}) {
  const registry = usePluginRegistry();
  // Subscribe to plugins Redux state to re-render when plugins are toggled
  useSelector((state: RootState) => state.pluginsToggle?.plugins);
  const pluginRoutes: WebRoute[] = registry.getWebRoutes();

  const pluginLinks: SidebarLink[] = pluginRoutes
    .filter((r) => r.sidebar_label)
    .map((r) => ({
      path: r.path,
      label: r.sidebar_label,
      order: r.sidebar_order,
    }));

  return (
    <nav
      id="main-navigation"
      className="sidebar"
      role="navigation"
      aria-label="Main navigation"
      aria-hidden={open ? 'false' : 'true'}
    >
      <div className="sidebar__section">
        <ul className="sidebar__links">
          {CORE_ROUTES.map((route) => (
            <li key={route.path}>
              <NavLink
                to={route.path}
                className={navLinkClassName}
                end={route.path === '/'}
                onClick={onNavigate}
              >
                {route.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {pluginLinks.length > 0 && (
        <div className="sidebar__section">
          <div className="sidebar__divider" />
          <span className="sidebar__section-title">Features</span>
          <ul className="sidebar__links">
            {pluginLinks.map((route) => (
              <li key={route.path}>
                <NavLink to={route.path} className={navLinkClassName} onClick={onNavigate}>
                  {route.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sidebar__section sidebar__section--bottom">
        <div className="sidebar__divider" />
        <ul className="sidebar__links">
          <li>
            <NavLink to="/settings" className={navLinkClassName} onClick={onNavigate}>
              Settings
            </NavLink>
          </li>
        </ul>
      </div>
    </nav>
  );
}
